// Persistência do anúncio de release (release-announcement.vue). Dois estados,
// duas chaves:
//
//   - VISTO: o usuário encerrou o anúncio de qualquer forma (X, ESC, clique
//     fora, "Entendido", "Não mostrar novamente"). Grava a version e o anúncio
//     não volta até subir a version em CurrentRelease.js.
//   - ADIADO: o usuário pediu "Ver mais tarde". Some pelo resto do dia e volta
//     no dia seguinte — ainda na mesma version.
//
// O adiamento carrega a version junto para não vazar entre releases: adiar o
// release A não pode esconder o release B publicado no dia seguinte.
//
// Cada acesso a localStorage é resiliente (try/catch): em modo privado/quota
// cheia o acesso pode lançar — nesse caso logamos e degradamos (o anúncio
// reaparece no próximo reload), como faz @modules/global/utils/whatsnew-storage.

import { RELEASE_MODAL_STORAGE_KEY } from '@modules/global/constants/CurrentRelease'

export const RELEASE_MODAL_REMIND_KEY = 'release-modal-remind-later'

// Dia civil LOCAL, não UTC: em GMT-3 o UTC vira o dia às 21h, e o anúncio
// adiado à noite reapareceria poucas horas depois em vez de no dia seguinte.
const today = () => {
  const now = new Date()
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-')
}

export const markReleaseSeen = (version) => {
  if (!version) return
  try {
    localStorage.setItem(RELEASE_MODAL_STORAGE_KEY, version)
    localStorage.removeItem(RELEASE_MODAL_REMIND_KEY)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Release: falha ao salvar estado no localStorage', e)
  }
}

export const remindReleaseLater = (version) => {
  if (!version) return
  try {
    localStorage.setItem(
      RELEASE_MODAL_REMIND_KEY,
      JSON.stringify({ version, day: today() })
    )
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Release: falha ao adiar o anúncio no localStorage', e)
  }
}

const readSnooze = () => {
  try {
    const raw = localStorage.getItem(RELEASE_MODAL_REMIND_KEY)
    if (!raw) return null
    const snooze = JSON.parse(raw)
    if (!snooze || !snooze.version || !snooze.day) return null
    return snooze
  } catch (e) {
    // JSON corrompido ou storage indisponível: trata como "não adiado".
    return null
  }
}

// True enquanto ainda é o mesmo dia em que o usuário pediu "Ver mais tarde".
const isSnoozed = (version) => {
  const snooze = readSnooze()
  if (!snooze || snooze.version !== version) return false
  return snooze.day === today()
}

// Gate consumido pelo layout: o anúncio desta version não deve abrir.
export const isReleaseDismissed = (version) => {
  if (!version) return false
  try {
    if (localStorage.getItem(RELEASE_MODAL_STORAGE_KEY) === version) return true
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Release: falha ao ler estado no localStorage', e)
    return false
  }
  return isSnoozed(version)
}

// Gate consumido pelo layout: a data-limite de exibição do anúncio já passou.
// `expiresAt` é opcional (CurrentRelease.js) — sem ele, nunca expira. Mesma
// comparação textual YYYY-MM-DD de today() acima (sem lib de data): funciona
// porque o formato é sempre zero-padded de tamanho fixo. Fronteira exclusiva
// de propósito: o anúncio continua valendo o dia INTEIRO de expiresAt, só some
// no dia seguinte — evita cortar o anúncio na metade do dia por causa de fuso.
export const isReleaseExpired = (expiresAt) => {
  if (!expiresAt) return false
  return today() > expiresAt
}
